import { vnTemplate } from '@valnet/vnKnockoutJsTemplates/vnTemplate';
import { filterDate_template } from '~/components/templates/filterDate_template';
import { listPageSelect_template } from '~/components/templates/listPageSelect_template';
import { multipleSelector_template } from '@valnet/vnKnockoutJsTemplates/multipleSelector_template';
import { presetFilter_template } from '@valnet/vnKnockoutJsTemplates/presetFilter_template';
import { html, LpButton, LpLoadingOverlay, LpModuleGrid, LpModuleGridHeading, LpPageHeader, raw, } from '~/design-system';
import { renderRevenueInsightsDailyBreakdown } from '~/components/revenue-insights/daily-breakdown';
import { createRevenueInsightsChartState, destroyRevenueInsightsCharts, renderRevenueInsightsCharts, renderRevenueInsightsSummaryCharts, } from '~/components/revenue-insights/summary-charts';
import { createCopyLinkAction } from '~/utils/clipboard';
import { legacyIconNameToPhosphorIconName, setOptionalIconName } from '~/utils/iconName';
const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
    numeric: 'auto',
});
function parseLastRefreshedAt(value) {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
function formatLastRefreshedDateTime(date) {
    return date.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}
function formatLastRefreshedLabel(value, now) {
    const date = parseLastRefreshedAt(value);
    if (!date) {
        return '';
    }
    const elapsed = Math.max(0, now - date.getTime());
    const minute = 60_000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const dateTime = formatLastRefreshedDateTime(date);
    if (elapsed < minute) {
        return `Last refreshed just now · ${dateTime}`;
    }
    if (elapsed < hour) {
        return `Last refreshed ${relativeTimeFormatter.format(-Math.max(1, Math.floor(elapsed / minute)), 'minute')} · ${dateTime}`;
    }
    if (elapsed < day) {
        return `Last refreshed ${relativeTimeFormatter.format(-Math.max(1, Math.floor(elapsed / hour)), 'hour')} · ${dateTime}`;
    }
    return `Last refreshed ${relativeTimeFormatter.format(-Math.max(1, Math.round(elapsed / day)), 'day')} · ${dateTime}`;
}
function formatLastRefreshedTitle(value) {
    const date = parseLastRefreshedAt(value);
    if (!date) {
        return '';
    }
    return `Last refreshed ${formatLastRefreshedDateTime(date)}`;
}
export class revenueInsights_template extends vnTemplate {
    constructor() {
        super(...arguments);
        this.lastRefreshedClockInterval = null;
    }
    getTemplateHtml() {
        return /* html */ `

            ${LpPageHeader({
            title: "Revenue Insights",
            description: "Review conversion, monetization, and revenue performance over time.",
            icon: "chart-line-up",
            actions: LpButton({
                label: html `<span data-bind="text: copyLinkLabel"></span>`,
                prefixIcon: "link",
                size: "base",
                variant: "secondary",
                attributes: {
                    "data-bind": "click: onCopyLink",
                },
            }),
            controls: html `
                    <div class="insights-header-controls revenue-insights-header-controls" data-bind="css: { 'is-open': showMoreFilters }">
                        <div
                            class="insights-filters-backdrop"
                            data-bind="click: closeMoreFilters, visible: showMoreFilters"
                        ></div>
                        <div class="insights-filters-sheet">
                            <div class="insights-header-primary">
                                <div class="hidden">
                                    ${raw(this.presetFilter.getHtmlBuild())}
                                </div>
                                <div class="revenue-insights-filter-cell">
                                    <div class="lib-date-picker">
                                        ${raw(this.datesFilter.getHtmlBuild())}
                                    </div>
                                </div>
                                <div class="revenue-insights-filter-cell">
                                    ${raw(this.granularityFilter.getHtmlBuild())}
                                </div>
                                <div class="revenue-insights-filter-cell">
                                    ${raw(this.productFilter.getHtmlBuild())}
                                </div>
                                <div class="revenue-insights-filter-cell">
                                    ${raw(this.partnerFilter.getHtmlBuild())}
                                </div>
                                <div class="revenue-insights-filter-cell">
                                    ${raw(this.buyerFilter.getHtmlBuild())}
                                </div>
                                <div class="revenue-insights-filter-cell">
                                    ${raw(this.trafficFilter.getHtmlBuild())}
                                </div>
                                <div class="revenue-insights-filter-cell">
                                    ${raw(this.channelFilter.getHtmlBuild())}
                                </div>
                                <div class="insights-advanced-filters-trigger">
                                    ${LpButton({
                label: "Advanced Filters",
                prefixIcon: "funnel",
                size: "base",
                variant: "ghost",
                attributes: {
                    "aria-controls": "revenue-insights-advanced-filters",
                    "data-bind": "click: toggleMoreFilters, clickBubble: false, attr: { 'aria-expanded': showMoreFilters() ? 'true' : 'false' }",
                },
            })}
                                </div>
                            </div>
                            <div
                                id="revenue-insights-advanced-filters"
                                class="insights-filters-secondary insights-global-filters"
                            >
                                <header class="insights-global-filters__header">
                                    <h2 class="insights-global-filters__title">Advanced Filters</h2>
                                    ${LpButton({
                ariaLabel: "Close filters",
                prefixIcon: "x",
                size: "sm",
                shape: "square",
                variant: "ghost",
                className: "insights-global-filters__icon-btn",
                attributes: {
                    "data-bind": "click: closeMoreFilters",
                },
            })}
                                </header>
                                <div class="insights-global-filters__list insights-advanced-filter-groups">
                                    ${raw(this.getDisplayFiltersHtml())}
                                </div>
                            </div>
                        </div>
                        <div class="insights-header-actions">
                            ${raw(this.getHeaderActionsHtml())}
                        </div>
                    </div>
                `,
        })}

            <div class="filter-bar">
                <div class="filter-bar__layout">
                    <div id="filters-selected" class="filters-selected">
                        ${raw(this.getSelectedFilterContainersHtml())}
                    </div>
                </div>
            </div>

			<div class="insights-results-shell" data-bind="css: { 'is-loading': requestState() === 'loading' }">
				${LpLoadingOverlay({
            label: 'Loading revenue insights...',
            loaderLabel: 'Loading revenue insights',
            className: 'insights-loading-state',
            attributes: {
                'data-bind': "visible: requestState() === 'loading'",
            },
        })}
				${LpModuleGrid({
            columns: 1,
            className: 'revenue-insights-module-grid',
            children: html `
						${LpModuleGridHeading({ children: html `<span data-bind="text: breakdownTitle"></span>` })}
						${renderRevenueInsightsDailyBreakdown()}
						${LpModuleGridHeading({
                children: 'Performance Charts',
                attributes: {
                    'data-bind': "visible: requestState() !== 'loading'",
                    style: 'display: none;',
                },
            })}
						${renderRevenueInsightsSummaryCharts()}
					`,
        })}
			</div>

		`;
    }
    getDisplayFiltersHtml() {
        return [
            {
                title: "General",
                filters: [
                    this.brandFilter.getHtmlBuild(),
                    this.deviceFilter.getHtmlBuild(),
                    this.leadTypeFilter.getHtmlBuild(),
                    this.formVariantFilter.getHtmlBuild(),
                    this.audienceFilter.getHtmlBuild(),
                ],
            },
            {
                title: "Auction Experiments",
                filters: [
                    this.clickAuctionExperimentFilter.getHtmlBuild(),
                    this.leadAuctionExperimentFilter.getHtmlBuild(),
                ],
            },
        ].map(({ title, filters }) => (this.getAdvancedFilterGroup(title, filters))).join("");
    }
    getHeaderActionsHtml() {
        return String(html `
			${LpButton({
            label: "Refresh",
            prefixIcon: "arrows-clockwise",
            size: "base",
            variant: "refresh",
            attributes: {
                "data-bind": "click: onRetryData, disable: requestState() === 'loading'",
            },
        })}

			${LpButton({
            label: "Reset",
            prefixIcon: "arrows-clockwise",
            variant: "primary",
            attributes: {
                "data-bind": "click: resetFilters",
            },
        })}
		`);
    }
    getSelectedFilterContainersHtml() {
        return [
            "brands",
            "products",
            "partners",
            "buyers",
            "traffic",
            "channels",
            "devices",
            "leadtypes",
            "formvariants",
            "audiences",
            "click-auction-experiments",
            "lead-auction-experiments",
        ].map((id) => `<div id="revenue-insights-selected-${id}"></div>`).join("");
    }
    getAdvancedFilterGroup(title, filters) {
        return /* html */ `
			<section class="insights-advanced-filter-group">
				<h3 class="insights-advanced-filter-group__title">${title}</h3>
				<div class="insights-advanced-filter-group__list">
					${filters.map((bodyHtml) => this.getAdvancedFilterControl(bodyHtml)).join("")}
				</div>
			</section>
		`;
    }
    getAdvancedFilterControl(bodyHtml) {
        return /* html */ `
			<div class="insights-advanced-filter">
				${bodyHtml}
			</div>
		`;
    }
    initPartial() {
        const vm = this.getTemplateViewModel();
        this.presetFilter = new presetFilter_template('presetFilter', vm, this.caller);
        this.presetFilter.setType('revenue_insights');
        this.brandFilter = new multipleSelector_template('brandFilter', vm, this.caller);
        this.brandFilter.setLabel('brands');
        setOptionalIconName(this.brandFilter, legacyIconNameToPhosphorIconName('brand'));
        this.brandFilter.setFilterContainerClass('lib-dropdown');
        this.brandFilter.setTagContainerId('revenue-insights-selected-brands');
        this.brandFilter.setTagContainerCaption('brands:');
        this.productFilter = new multipleSelector_template('productFilter', vm, this.caller);
        this.productFilter.setLabel('products');
        setOptionalIconName(this.productFilter, legacyIconNameToPhosphorIconName('product'));
        this.productFilter.setFilterContainerClass('lib-dropdown');
        this.productFilter.setTagContainerId('revenue-insights-selected-products');
        this.productFilter.setTagContainerCaption('products:');
        this.partnerFilter = new multipleSelector_template('partnerFilter', vm, this.caller);
        this.partnerFilter.setLabel('partners');
        setOptionalIconName(this.partnerFilter, legacyIconNameToPhosphorIconName('users'));
        this.partnerFilter.setFilterContainerClass('lib-dropdown');
        this.partnerFilter.setTagContainerId('revenue-insights-selected-partners');
        this.partnerFilter.setTagContainerCaption('partners:');
        this.buyerFilter = new multipleSelector_template('buyerFilter', vm, this.caller);
        this.buyerFilter.setLabel('lead buyer');
        setOptionalIconName(this.buyerFilter, legacyIconNameToPhosphorIconName('users'));
        this.buyerFilter.setFilterContainerClass('lib-dropdown');
        this.buyerFilter.setTagContainerId('revenue-insights-selected-buyers');
        this.buyerFilter.setTagContainerCaption('buyers:');
        this.trafficFilter = new multipleSelector_template('trafficFilter', vm, this.caller);
        this.trafficFilter.setLabel('traffic source');
        setOptionalIconName(this.trafficFilter, legacyIconNameToPhosphorIconName('link'));
        this.trafficFilter.setFilterContainerClass('lib-dropdown');
        this.trafficFilter.setTagContainerId('revenue-insights-selected-traffic');
        this.trafficFilter.setTagContainerCaption('traffic:');
        this.channelFilter = new multipleSelector_template('channelFilter', vm, this.caller);
        this.channelFilter.setLabel('distribution channel');
        setOptionalIconName(this.channelFilter, legacyIconNameToPhosphorIconName('channel-share'));
        this.channelFilter.setFilterContainerClass('lib-dropdown');
        this.channelFilter.setTagContainerId('revenue-insights-selected-channels');
        this.channelFilter.setTagContainerCaption('channels:');
        this.deviceFilter = new multipleSelector_template('deviceFilter', vm, this.caller);
        this.deviceFilter.setLabel('device');
        setOptionalIconName(this.deviceFilter, legacyIconNameToPhosphorIconName('device'));
        this.deviceFilter.setFilterContainerClass('lib-dropdown');
        this.deviceFilter.setTagContainerId('revenue-insights-selected-devices');
        this.deviceFilter.setTagContainerCaption('devices:');
        this.leadTypeFilter = new multipleSelector_template('leadTypeFilter', vm, this.caller);
        this.leadTypeFilter.setLabel('lead type');
        setOptionalIconName(this.leadTypeFilter, 'identification-card');
        this.leadTypeFilter.setFilterContainerClass('lib-dropdown');
        this.leadTypeFilter.setTagContainerId('revenue-insights-selected-leadtypes');
        this.leadTypeFilter.setTagContainerCaption('lead types:');
        this.formVariantFilter = new multipleSelector_template('formVariantFilter', vm, this.caller);
        this.formVariantFilter.setLabel('form variant');
        setOptionalIconName(this.formVariantFilter, 'clipboard-text');
        this.formVariantFilter.setFilterContainerClass('lib-dropdown');
        this.formVariantFilter.setTagContainerId('revenue-insights-selected-formvariants');
        this.formVariantFilter.setTagContainerCaption('form variants:');
        this.audienceFilter = new multipleSelector_template('audienceFilter', vm, this.caller);
        this.audienceFilter.setLabel('audience');
        setOptionalIconName(this.audienceFilter, 'target');
        this.audienceFilter.setFilterContainerClass('lib-dropdown');
        this.audienceFilter.setTagContainerId('revenue-insights-selected-audiences');
        this.audienceFilter.setTagContainerCaption('audiences:');
        this.clickAuctionExperimentFilter = new multipleSelector_template('clickAuctionExperimentFilter', vm, this.caller);
        this.clickAuctionExperimentFilter.setLabel('click auction experiment');
        setOptionalIconName(this.clickAuctionExperimentFilter, 'cursor-click');
        this.clickAuctionExperimentFilter.setFilterContainerClass('lib-dropdown');
        this.clickAuctionExperimentFilter.setTagContainerId('revenue-insights-selected-click-auction-experiments');
        this.clickAuctionExperimentFilter.setTagContainerCaption('click auction experiments:');
        this.leadAuctionExperimentFilter = new multipleSelector_template('leadAuctionExperimentFilter', vm, this.caller);
        this.leadAuctionExperimentFilter.setLabel('lead auction experiment');
        setOptionalIconName(this.leadAuctionExperimentFilter, legacyIconNameToPhosphorIconName('bar-graph'));
        this.leadAuctionExperimentFilter.setFilterContainerClass('lib-dropdown');
        this.leadAuctionExperimentFilter.setTagContainerId('revenue-insights-selected-lead-auction-experiments');
        this.leadAuctionExperimentFilter.setTagContainerCaption('lead auction experiments:');
        this.datesFilter = new filterDate_template('datesFilter', this.getTemplateViewModel(), this.caller);
        this.datesFilter.setLabel('');
        this.datesFilter.setStartPickerId('revenue-insights-start-date-picker');
        this.datesFilter.setEndPickerId('revenue-insights-end-date-picker');
        this.granularityFilter = new listPageSelect_template('revenueInsightsGranularity', vm, this.caller);
        this.granularityFilter.setLabel('breakdown');
        this.granularityFilter.setIconName('calendar');
        this.granularityFilter.setShowLabelPrefix(true);
        this.granularityFilter.setOptions([
            { id: 'daily', name: 'Daily' },
            { id: 'hourly', name: 'Hourly' },
        ]);
        this.granularityFilter.bindValue(vm.granularity);
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        let firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        if (date.getTime() === firstOfMonth.getTime()) {
            firstOfMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
            date.setDate(date.getDate() - 1);
        }
        else {
            date.setDate(date.getDate() - 1);
        }
        this.datesFilter.setDateRange(firstOfMonth, date);
        this.lastRefreshedClockInterval = window.setInterval(() => {
            vm.lastRefreshedNow(Date.now());
        }, 60_000);
    }
    getTemplateName() {
        return 'revenueInsights_template';
    }
    getMainElementClasses() {
        return ['w-content', 'revenue-insights-page'];
    }
    activateLoading() {
        const viewModel = this.getTemplateViewModel();
        viewModel.requestError('');
        viewModel.requestState('loading');
    }
    completeLoading() {
        const viewModel = this.getTemplateViewModel();
        viewModel.hasLoadedData(true);
        viewModel.requestError('');
        viewModel.requestState('ready');
    }
    failLoading(message) {
        const viewModel = this.getTemplateViewModel();
        viewModel.requestError(message);
        viewModel.requestState('error');
    }
    setExporting(isExporting) {
        this.getTemplateViewModel().isExporting(isExporting);
    }
    buildViewModel() {
        const self = this;
        const hideFunnelMetrics = ko.observable(false);
        const { copyLinkLabel, onCopyLink } = createCopyLinkAction(ko);
        const lastRefreshedAt = ko.observable(null);
        const lastRefreshedNow = ko.observable(Date.now());
        const totalsData = ko.observableArray([]);
        const listData = ko.observableArray([]);
        const granularity = ko.observable("daily");
        const chartState = createRevenueInsightsChartState(ko, listData, totalsData);
        const breakdownTitle = ko.pureComputed(() => granularity() === "hourly" ? "Hourly Breakdown" : "Daily Breakdown");
        const breakdownCountLabel = ko.pureComputed(() => {
            const count = listData().length;
            if (granularity() === "hourly") {
                return count === 1 ? "Showing 1 hour" : `Showing ${count} hours`;
            }
            return count === 1 ? "Showing 1 day" : `Showing ${count} days`;
        });
        const lastRefreshedLabel = ko.pureComputed(() => formatLastRefreshedLabel(lastRefreshedAt(), lastRefreshedNow()));
        const lastRefreshedTitle = ko.pureComputed(() => formatLastRefreshedTitle(lastRefreshedAt()));
        return {
            copyLinkLabel,
            onCopyLink,
            lastRefreshedAt,
            lastRefreshedNow,
            lastRefreshedLabel,
            lastRefreshedTitle,
            requestState: ko.observable('loading'),
            requestError: ko.observable(''),
            hasLoadedData: ko.observable(false),
            isExporting: ko.observable(false),
            orderBy: ko.observable("dateData"),
            orderByDirection: ko.observable("ASC"),
            orderByAndDirection: ko.observable("dateData_ASC"),
            granularity,
            breakdownTitle,
            breakdownCountLabel,
            hideFunnelMetrics,
            totalsData,
            listData,
            ...chartState,
            showMoreFilters: ko.observable(false),
            resetFilters: function (data) { self.resetFilters(); },
            resetDisplayFilters: function () { self.resetDisplayFilters(); },
            toggleMoreFilters: function () {
                const vm = self.getTemplateViewModel();
                const next = !vm.showMoreFilters();
                vm.showMoreFilters(next);
            },
            closeMoreFilters: function () {
                const vm = self.getTemplateViewModel();
                vm.showMoreFilters(false);
            },
            onRetryData() { self.caller.getListData(); },
            onClickReorder(value) { self.onClickReorder(value); },
            formatNumeric: function (value, digits) { return self.formatNumeric(value, digits); },
            formatMoney: function (value, digits) { return self.formatMoney(value, digits); },
            onExportData() {
                self.onExportData();
            },
        };
    }
    getViewModelForRequest() {
        return {
            orderBy: this.getTemplateViewModel().orderBy(),
            orderByDirection: this.getTemplateViewModel().orderByDirection(),
            granularity: this.getTemplateViewModel().granularity(),
            filters: {
                granularity: this.getTemplateViewModel().granularity(),
                selectedBrands: this.brandFilter.getSelectedItems().map((itemId) => ({ id: itemId })),
                selectedProducts: this.productFilter.getSelectedItems(),
                selectedPartners: this.partnerFilter.getSelectedItems(),
                selectedBuyers: this.buyerFilter.getSelectedItems(),
                selectedTraffics: this.trafficFilter.getSelectedItems(),
                selectedChannels: this.channelFilter.getSelectedItems(),
                selectedDevices: this.deviceFilter.getSelectedItems(),
                selectedLeadTypes: this.leadTypeFilter.getSelectedItems(),
                selectedFormVariants: this.formVariantFilter.getSelectedItems(),
                selectedAudiences: this.audienceFilter.getSelectedItems(),
                selectedClickAuctionExperiments: this.clickAuctionExperimentFilter.getSelectedItems(),
                selectedLeadAuctionExperiments: this.leadAuctionExperimentFilter.getSelectedItems(),
                dateFilterType: this.datesFilter.getTemplateViewModel().selectedFilter(),
                startDate: this.datesFilter.getTemplateViewModel().startDate(),
                endDate: this.datesFilter.getTemplateViewModel().endDate(),
                startDateRange: this.datesFilter.getTemplateViewModel().fromRange(),
                endDateRange: this.datesFilter.getTemplateViewModel().toRange(),
            },
        };
    }
    buildDatePickerCalendar() {
        this.datesFilter.buildCalendarWhenReady();
    }
    destroyDatePickerCalendar() {
        this.datesFilter.destroyCalendars();
    }
    destroyLastRefreshedClock() {
        if (this.lastRefreshedClockInterval === null) {
            return;
        }
        window.clearInterval(this.lastRefreshedClockInterval);
        this.lastRefreshedClockInterval = null;
    }
    destroySummaryCharts() {
        destroyRevenueInsightsCharts();
    }
    setBrandFilterItems(brands) {
        this.setMultiSelectItems(this.brandFilter, brands || []);
    }
    setProductFilterItems(items) {
        this.setMultiSelectItems(this.productFilter, items || []);
    }
    setPartnerFilterItems(items) {
        this.setMultiSelectItems(this.partnerFilter, items || []);
    }
    setBuyerFilterItems(items) {
        this.setMultiSelectItems(this.buyerFilter, items || []);
    }
    setTrafficFilterItems(items) {
        this.setMultiSelectItems(this.trafficFilter, items || []);
    }
    setChannelFilterItems(items) {
        this.setMultiSelectItems(this.channelFilter, items || []);
    }
    setDeviceFilterItems(items) {
        this.setMultiSelectItems(this.deviceFilter, items || []);
    }
    setLeadTypeFilterItems(items) {
        this.setMultiSelectItems(this.leadTypeFilter, items || []);
    }
    setFormVariantFilterItems(items) {
        this.setMultiSelectItems(this.formVariantFilter, items || []);
    }
    setAudienceFilterItems(items) {
        this.setMultiSelectItems(this.audienceFilter, items || []);
    }
    setClickAuctionExperimentFilterItems(items) {
        this.setMultiSelectItems(this.clickAuctionExperimentFilter, items || []);
    }
    setLeadAuctionExperimentFilterItems(items) {
        this.setMultiSelectItems(this.leadAuctionExperimentFilter, items || []);
    }
    setTotalsData(totalsData) {
        const viewModel = this.getTemplateViewModel();
        viewModel.totalsData(totalsData);
        renderRevenueInsightsCharts(viewModel.listData(), totalsData, viewModel.selectedChartMetric());
    }
    setListData(listData) {
        const viewModel = this.getTemplateViewModel();
        viewModel.listData(listData);
        renderRevenueInsightsCharts(listData, viewModel.totalsData(), viewModel.selectedChartMetric());
    }
    setLastRefreshedAt(lastRefreshedAt) {
        this.getTemplateViewModel().lastRefreshedAt(lastRefreshedAt);
    }
    setStatus(status) {
        const maxDateCreated = status?.maxDateCreated ?? null;
        this.setLastRefreshedAt(maxDateCreated);
    }
    setMultiSelectItems(filter, items) {
        filter.setItems(items);
        //filter.setSeletedItemIds([]);
    }
    setViewModelFromRequest(params) {
        let selectedBrands = params['selectedBrands'];
        if (typeof selectedBrands !== 'undefined' && !Array.isArray(selectedBrands)) {
            selectedBrands = [selectedBrands];
        }
        if (Array.isArray(selectedBrands)) {
            let normalizedSelectedBrands = selectedBrands.map((item) => {
                if (typeof item === 'object' && item !== null && typeof item['id'] !== 'undefined') {
                    return item['id'];
                }
                return item;
            });
            this.presetFilter.setMultiSelectorPresetFilters(this.brandFilter, normalizedSelectedBrands);
        }
        this.presetFilter.setMultiSelectorPresetFilters(this.productFilter, params['selectedProducts']);
        this.presetFilter.setMultiSelectorPresetFilters(this.partnerFilter, params['selectedPartners']);
        this.presetFilter.setMultiSelectorPresetFilters(this.buyerFilter, params['selectedBuyers']);
        this.presetFilter.setMultiSelectorPresetFilters(this.trafficFilter, params['selectedTraffics']);
        this.presetFilter.setMultiSelectorPresetFilters(this.channelFilter, params['selectedChannels']);
        this.presetFilter.setMultiSelectorPresetFilters(this.deviceFilter, params['selectedDevices']);
        this.presetFilter.setMultiSelectorPresetFilters(this.leadTypeFilter, params['selectedLeadTypes']);
        this.presetFilter.setMultiSelectorPresetFilters(this.formVariantFilter, params['selectedFormVariants']);
        this.presetFilter.setMultiSelectorPresetFilters(this.audienceFilter, params['selectedAudiences']);
        this.presetFilter.setMultiSelectorPresetFilters(this.clickAuctionExperimentFilter, params['selectedClickAuctionExperiments']);
        this.presetFilter.setMultiSelectorPresetFilters(this.leadAuctionExperimentFilter, params['selectedLeadAuctionExperiments']);
        this.presetFilter.setDates(this.datesFilter, params['dateFilterType'], params['startDate'], params['endDate'], params['startDateRange'], params['endDateRange']);
        this.getTemplateViewModel().granularity(params['granularity'] === 'hourly' ? 'hourly' : 'daily');
        this.applyLeadTypeColumnVisibility();
    }
    onClickReorder(value) {
        const viewModel = this.getTemplateViewModel();
        const direction = viewModel.orderBy() === value && viewModel.orderByDirection() === "DESC"
            ? "ASC"
            : "DESC";
        viewModel.orderBy(value);
        viewModel.orderByDirection(direction);
        this.caller.getListData();
    }
    applyLeadTypeColumnVisibility() {
        const items = this.leadTypeFilter.getSelectedItems();
        const hide = items.length > 0;
        this.getTemplateViewModel().hideFunnelMetrics(hide);
    }
    formatNumeric(value, digits = 0) {
        let n = Number(value);
        if (isNaN(n)) {
            n = 0;
        }
        return n.toLocaleString('en-EG', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    }
    formatMoney(value, digits = 0) {
        return '$' + this.formatNumeric(value, digits);
    }
    clearMultiFilterTags(filter) {
        const tvm = filter.getTemplateViewModel();
        filter.setSeletedItemIds([]);
        tvm.selectedItems([]);
        tvm.selectedGroups([]);
        tvm.selectedItemsOrderBySelection([]);
        tvm.openedGroupsDrawers([]);
        const el = filter.getTagContainer();
        if (el) {
            el.innerHTML = '';
        }
    }
    onExportData() {
        this.caller.getExportData();
    }
    resetFilters() {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        let firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        if (date.getTime() === firstOfMonth.getTime()) {
            firstOfMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
            date.setDate(date.getDate() - 1);
        }
        else {
            date.setDate(date.getDate() - 1);
        }
        this.datesFilter.setDateRange(firstOfMonth, date);
        this.clearMultiFilterTags(this.brandFilter);
        this.clearMultiFilterTags(this.productFilter);
        this.clearMultiFilterTags(this.partnerFilter);
        this.clearMultiFilterTags(this.buyerFilter);
        this.clearMultiFilterTags(this.trafficFilter);
        this.resetDisplayFilters(false);
        this.getTemplateViewModel().orderBy("dateData");
        this.getTemplateViewModel().orderByDirection("ASC");
        this.getTemplateViewModel().granularity("daily");
        this.applyLeadTypeColumnVisibility();
        this.caller.getListData();
    }
    resetDisplayFilters(reload = true) {
        this.clearMultiFilterTags(this.brandFilter);
        this.clearMultiFilterTags(this.productFilter);
        this.clearMultiFilterTags(this.partnerFilter);
        this.clearMultiFilterTags(this.buyerFilter);
        this.clearMultiFilterTags(this.trafficFilter);
        this.clearMultiFilterTags(this.channelFilter);
        this.clearMultiFilterTags(this.deviceFilter);
        this.clearMultiFilterTags(this.leadTypeFilter);
        this.clearMultiFilterTags(this.formVariantFilter);
        this.clearMultiFilterTags(this.audienceFilter);
        this.clearMultiFilterTags(this.clickAuctionExperimentFilter);
        this.clearMultiFilterTags(this.leadAuctionExperimentFilter);
        this.getTemplateViewModel().orderBy("dateData");
        this.getTemplateViewModel().orderByDirection("ASC");
        this.getTemplateViewModel().orderByAndDirection("dateData_ASC");
        this.getTemplateViewModel().granularity("daily");
        this.applyLeadTypeColumnVisibility();
        if (reload) {
            this.caller.getListData();
        }
    }
}
